<template>
  <div class="relative map-wrap">
    <div id="search-result-map" class="w-full h-full pc:rounded-[20px]"/>
    <Transition name="slide-fade">
      <MobileMapSpaceList v-if="isMobile"
                          ref="refMobileMapSpaceList"
                          :company-list=spaceList
                          @onCompanySelected="refreshSelectedMarker"/>
    </Transition>
    <div class="re-search"
         @click="geoSearch">
      {{ $t('common.re_search') }}
    </div>
  </div>
</template>

<script setup>

import {computed, onBeforeMount, reactive, ref, toRaw, watch} from "vue";
import {useSettingStore} from "@/stores/setting.js";
import {useSearchResultStore} from "~/stores/searchResult.ts";
import {useRoute, useRouter} from "nuxt/app";
import {useSearchQueryStore} from "@/stores/searchQuery.js";
import {useSearchWithStore} from "@/composables/useSearchWithStore";
import MobileMapSpaceList from "@/components/MobileMapSpaceList.vue";
import {parseFormatCheapestRentPrice} from "~/utils/newPriceTool.ts";

const { $loadGoogleMaps } = useNuxtApp()
const router = useRouter()
const route = useRoute()
const {handleSearchWithStore} = useSearchWithStore()
const settingStore = useSettingStore()
const searchResultStore = useSearchResultStore()
const searchQueryStore = useSearchQueryStore()

const refMobileMapSpaceList = ref(null)

const isMobile = computed(() => settingStore.isMobile);
/// 搜尋結果列表
const spaceList = computed(() => searchResultStore.getSpaceList);
/// 選中空間 space_id
const routeSid = computed(() => route.query.msid)


const states = reactive({
  map: null,
  markers: []
});

// 確保 initMap 只被 call 一次
let mapInitialized = false;
onBeforeMount(async () => {
  if(process.client && !mapInitialized){
    await initMap()
    mapInitialized = true
  }
})


const initMap = async () => {
  try {
    const googleMaps = await $loadGoogleMaps()
    const { Map } = await googleMaps.importLibrary('maps')

    const mapOptions = {
      mapId: 'DEMO_MAP_ID',
      center: { lat: 25.044351, lng: 121.516038 },
      zoom: 14,
      maxZoom: 18,
      minZoom: 4,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
      zoomControl: !isMobile,
    }

    states.map = new Map(document.getElementById('search-result-map'), mapOptions)
  } catch (error) {
    console.error('Google Maps 初始化失敗', error)
  }
};

async function geoSearch() {
  const {latitude, longitude, width, height} = await getMapBounds();
  searchQueryStore.setGeoSearch({
    longitude: longitude,
    latitude: latitude,
    width: width,
    height: height
  })
  searchQueryStore.clearCitySelected()
  searchQueryStore.setPagination({sort: 'location', dir: 0})

  await handleSearchWithStore()
}

const getMapBounds = async () => {
  let latitude = route.query.latitude || '25.044351';
  let longitude = route.query.longitude || '121.516038';
  let width = 9000;
  let height = 9000;
  const bounds = await states.map.getBounds();

  if (bounds) {
    const center = states.map.getCenter(); // 中心點
    const {spherical} = await google.maps.importLibrary("geometry");
    const northEast = bounds.getNorthEast();
    const southWest = bounds.getSouthWest();

    // 這兩點的緯度相同，所以寬度就是這兩點之間的經度差的距離
    width = spherical.computeDistanceBetween(
        new google.maps.LatLng(southWest.lat(), southWest.lng()),
        new google.maps.LatLng(southWest.lat(), northEast.lng())
    );

    // 這兩點的經度相同，所以高度就是這兩點之間的緯度差的距離
    height = spherical.computeDistanceBetween(
        new google.maps.LatLng(southWest.lat(), southWest.lng()),
        new google.maps.LatLng(northEast.lat(), southWest.lng())
    );

    latitude = center.lat();
    longitude = center.lng();
  }

  return {latitude, longitude, width, height};
}

watch(() => [states.map, spaceList.value], () => {
  if (states.map) {
    addMarker()
  }
})

async function addMarker() {
  const {AdvancedMarkerElement} = await google.maps.importLibrary("marker");
  cleanMarkers()

  spaceList.value?.forEach((space, index) => {
    /// 加入標記
    const priceTag = document.createElement("div");
    priceTag.className = "price-tag";
    priceTag.textContent = parseFormatCheapestRentPrice(space.rent.rent_groups)
    priceTag.dataset.msid = space.space_id.toString();
    const marker = new AdvancedMarkerElement({
      map: toRaw(states.map),
      position: {lat: parseFloat(space.latitude), lng: parseFloat(space.longitude)},
      title: space.admin_id.toString(),
      content: priceTag,
      zIndex: 1 // 初始 zIndex
    });

    /// 點擊事件
    marker.addListener("click", () => {
      marker.zIndex = 100; // 設定最高 zIndex
      if (isMobile.value) {
        refMobileMapSpaceList.value.slideTo(index)
      } else {
        // 點擊標記，網址加上 sid (開啟空間詳情)
        router.replace({query: {'msid': space.space_id, 'slideTo': true}})
      }
    });

    /// 新增標記
    states.markers.push(marker);
  })

  /// 根據標記，顯示地圖範圍
  const bounds = new google.maps.LatLngBounds();
  spaceList.value?.map(space => {
    return new google.maps.LatLng(space.latitude, space.longitude)
  }).forEach(function (latLng) {
    bounds.extend(latLng);
  });
  if (spaceList.value?.length > 0) {
    states.map.fitBounds(bounds, getPaddingOptions());
  }
}

/// 連動網址 msid 選中 marker
watch(() => routeSid.value, () => {
  refreshSelectedMarker()
})

function refreshSelectedMarker(index = -1) {
  states.markers.forEach((marker, i) => {
    if (toRaw(marker).content.dataset.msid === routeSid.value || i === index) {
      toRaw(marker).content.classList.add('price-tag-selected')
      toRaw(marker).zIndex = 100;
      panToPosition(toRaw(marker).position);
    } else {
      toRaw(marker).content.classList.remove('price-tag-selected')
      toRaw(marker).zIndex = 1;
    }
  })
}

function panToPosition(position) {
  /// todo 手機版位移調整
  const paddingOptions = getPaddingOptions()

  // states.map.setZoom(14)
  states.map.panTo(position);
  states.map.panBy((paddingOptions.right - paddingOptions.left) / 2, (paddingOptions.bottom - paddingOptions.top) / 2);
}

function getPaddingOptions() {
  return isMobile.value ?
      {
        top: 0,
        bottom: window.screen.width * 0.57,
        left: 0,
        right: 0
      } :
      {
        top: 0,
        bottom: 0,
        left: routeSid.value ? 0 : 0,
        right: 0
      }
}

function cleanMarkers() {
  states.markers.forEach(marker => {
    toRaw(marker).setMap(null);
  })
  states.markers.length = 0
}
</script>

<style>
.price-tag {
  @apply bg-white rounded-[8px] text-black text-center text-[16px] px-[12px] py-[6px] cursor-pointer border-osn-black-00 border-[2px];
}

.price-tag-selected {
  @apply bg-osn-yellow-00 font-bold;
}

</style>

<style scoped lang="scss">
/*點擊地圖會有藍框*/
:deep(.gm-style iframe + div) {
  border: none !important;
}

.map-wrap {
  @apply absolute top-0 bottom-0 left-0 right-0 rounded-[20px];
}

.re-search {
  @apply absolute bottom-[40px] left-1/2 translate-x-[-50%] min-w-[103px] h-[36px] leading-[36px] px-[30px] rounded-[48px] text-black text-center bg-osn-white-00 cursor-pointer
  mobile:bottom-[240px];
}
</style>
